import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardPaste, AlertTriangle } from "lucide-react";
import { parsePaste, FIELD_LABEL } from "./pasteParse";
import { lineCurrency } from "./multiEntry";

const cell = (bad) => `px-2 py-1.5 truncate ${bad ? "text-[#E63946] bg-[#E63946]/5" : ""}`;

function PreviewTable({ lines, ctx }) {
  const accName = (id) => ctx.accounts.find((a) => a.id === id)?.name;
  const cols = ctx.isBusiness
    ? ["date", "type", "category", "coa_code", "account_id", "note", "amount"]
    : ["date", "type", "category", "account_id", "note", "amount"];
  const show = (l, f) => {
    if (l.unmatched[f]) return `? ${l.unmatched[f]}`;
    if (f === "type") return ctx.isBusiness ? l.business_kind : l.type;
    if (f === "account_id") return accName(l.account_id) || "Unassigned";
    if (f === "coa_code") return l.coa_code === "none" ? "—" : l.coa_code;
    if (f === "amount") return l.amount ? `${lineCurrency(l, ctx.accounts, ctx.currency)} ${l.amount}` : "";
    return l[f] || "";
  };
  return (
    <div className="max-h-[40vh] overflow-auto rounded-lg border border-[var(--divider)]">
      <table className="w-full text-xs" data-testid="paste-preview-table">
        <thead className="sticky top-0 bg-[var(--card)]">
          <tr>{cols.map((c) => (
            <th key={c} className="px-2 py-1.5 text-left text-[10px] tracking-[0.15em] uppercase text-[var(--muted-text)]">{FIELD_LABEL[c]}</th>
          ))}</tr>
        </thead>
        <tbody>
          {lines.slice(0, 100).map((l, i) => (
            <tr key={l.key} data-testid={`paste-preview-row-${i}`} className="border-t border-[var(--divider)]">
              {cols.map((c) => (
                <td key={c} className={`${cell(!!l.unmatched[c])} ${c === "amount" ? "text-right font-mono" : ""} max-w-[180px]`}>{show(l, c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PasteDialog({ open, onOpenChange, initialText, ctx, onConfirm }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(initialText || ""); }, [open, initialText]);
  const parsed = useMemo(() => parsePaste(text, ctx), [text, ctx]);
  const flagged = parsed.lines.filter((l) => Object.keys(l.unmatched).length).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="paste-dialog" className="w-[94vw] max-w-[960px] sm:max-w-[960px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardPaste className="w-4 h-4" /> Paste from spreadsheet</DialogTitle>
          <DialogDescription className="text-xs">
            Copy rows from Excel or Google Sheets and paste below. A header row (Date, Type, Category, Account, Memo, Amount) is matched by name; otherwise columns follow the grid order.
          </DialogDescription>
        </DialogHeader>
        <Textarea data-testid="paste-textarea" rows={5} value={text} autoFocus
                  placeholder={"Date\tType\tCategory\tAccount\tMemo\tAmount\n2026-06-01\tExpense\tRent\tChecking\tJune rent\t1200"}
                  onChange={(e) => setText(e.target.value)} className="font-mono text-xs" />
        {parsed.lines.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid="paste-mapping">
              <span className="text-[var(--muted-text)]">{parsed.hasHeader ? "Header detected:" : "Grid order:"}</span>
              {parsed.columns.map((c, i) => (
                <span key={`col-${i}-${c || "x"}`} className={`px-2 py-0.5 rounded-full border ${c ? "border-[var(--divider)]" : "border-dashed text-[var(--muted-text)] line-through"}`}>
                  {c ? FIELD_LABEL[c] : `col ${i + 1}`}
                </span>
              ))}
            </div>
            <PreviewTable lines={parsed.lines} ctx={ctx} />
          </>
        )}
        <div className="flex items-center gap-3">
          <span data-testid="paste-summary" className="text-xs text-[var(--muted-text)] mr-auto flex items-center gap-1.5">
            {parsed.lines.length} line{parsed.lines.length === 1 ? "" : "s"} found
            {flagged > 0 && (
              <span className="text-[#E63946] inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {flagged} need a fix (red cells)
              </span>
            )}
          </span>
          <Button variant="ghost" data-testid="paste-cancel" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="paste-confirm" disabled={!parsed.lines.length}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                  onClick={() => onConfirm(parsed.lines)}>
            Add {parsed.lines.length || ""} line{parsed.lines.length === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
